// Stacked bar chart of sessions per month, coloured by source.

import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface Row {
  month: string;
  source: string;
  count: number;
}

const SOURCES = ['gymbook', 'setgraph', 'strava', 'bilbo'] as const;
const SOURCE_COLORS: Record<(typeof SOURCES)[number], string> = {
  gymbook: '#2ea44f',
  setgraph: '#3b82f6',
  strava: '#f97316',
  bilbo: '#a78bfa',
};

export function SessionsByMonth({ data }: { data: Row[] }) {
  const wide = useMemo(() => {
    const byMonth = new Map<string, Record<string, number | string>>();
    for (const r of data) {
      const row = byMonth.get(r.month) ?? { month: r.month };
      row[r.source] = (Number(row[r.source]) || 0) + r.count;
      byMonth.set(r.month, row);
    }
    return [...byMonth.values()].sort((a, b) =>
      String(a.month).localeCompare(String(b.month)),
    );
  }, [data]);

  return (
    <div style={{ width: '100%', height: 280 }}>
      <ResponsiveContainer>
        <BarChart data={wide} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
          <XAxis
            dataKey="month"
            stroke="#666"
            fontSize={11}
            tickFormatter={(v: string) => {
              // 'YYYY-MM' → 'Jan 22' (when at year boundary, show full year)
              const [y, m] = v.split('-').map(Number);
              if (!y || !m) return v;
              const month = new Date(y, m - 1, 1).toLocaleString('en', { month: 'short' });
              return m === 1 ? `${month} ${String(y).slice(-2)}` : month;
            }}
            minTickGap={20}
          />
          <YAxis stroke="#666" fontSize={11} allowDecimals={false} />
          <Tooltip
            cursor={{ fill: '#1a1a1a' }}
            contentStyle={{
              background: '#0f0f0f',
              border: '1px solid #2a2a2a',
              borderRadius: 8,
              fontSize: 12,
            }}
            labelStyle={{ color: '#ddd' }}
          />
          <Legend
            wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
            iconType="circle"
            iconSize={8}
          />
          {SOURCES.map((src) => (
            <Bar key={src} dataKey={src} stackId="a" fill={SOURCE_COLORS[src]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
